import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCurrentAnnouncements } from '@bonusly/common-announcements';
import { Box, HStack, MegaphoneOutline, Text } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';

import { useAnnouncementDismissals } from '../../../components/announcements/hooks';
import { AnnouncementActionEvent } from '../../../components/announcements/types/events';
import { Reveal } from '../../../components/display/reveal';
import { useCloseNavbarPanels } from '../../../hooks/use-navbar-panels';
import { UserNavAction } from './user-nav-action';

interface AnnouncementMenuItemProps {
	isExpanded: boolean;
	onClose?: () => void;
}

export const AnnouncementMenuItem: FC<AnnouncementMenuItemProps> = ({ isExpanded, onClose }) => {
	const { t } = useTranslation('navigation');
	const { data: announcements } = useGetCurrentAnnouncements();
	const { isAnnouncementDismissed } = useAnnouncementDismissals();
	const dispatch = useDispatch();
	const closeAllPanels = useCloseNavbarPanels();
	const hasActiveAnnouncements = announcements && announcements.length > 0;

	const unreadCount = useMemo(() => {
		if (!announcements) return 0;
		return announcements.filter((announcement) => !isAnnouncementDismissed(announcement.id)).length;
	}, [announcements, isAnnouncementDismissed]);

	if (!hasActiveAnnouncements) {
		return null;
	}

	const handleClick = () => {
		const firstAnnouncementId = announcements?.[0]?.id;
		if (firstAnnouncementId) {
			// NOTE: open to first announcement so it can be marked as read
			dispatch(AnnouncementActionEvent.OpenAnnouncement, { id: firstAnnouncementId });
		}
		closeAllPanels();
		onClose?.();
	};

	return (
		<UserNavAction onClick={handleClick} label={t('links.announcements')} isExpanded={isExpanded} data-testid="nav-link-announcements" mb={{ base: 0, sm: 3 }}>
			<HStack justifyContent={isExpanded ? 'flex-start' : 'center'} alignItems="center" spacing={3}>
				<Box position="relative" display="flex" alignItems="center">
					<MegaphoneOutline size="md" />
				</Box>
				<Reveal if={isExpanded}>
					<HStack justifyContent="space-between" alignItems="center" width="100%">
						<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
							{t('links.announcements')}
						</Text>
						<Reveal if={unreadCount > 0}>
							<Box h={6} w={6} bg="primary.bgAlpha" borderRadius="full" display="flex" justifyContent="center" alignItems="center">
								<Text color="primary.fg" fontSize={50} fontWeight="semibold" whiteSpace="nowrap" lineHeight={1}>
									{unreadCount}
								</Text>
							</Box>
						</Reveal>
					</HStack>
				</Reveal>
			</HStack>
		</UserNavAction>
	);
};
